import { cleanupParentContainers } from '@/lib/blocks/cleanup';
import { FLOAT_RESTORE_POSITION_PROP, isBlockFloated } from '@/lib/blocks/layout/group-layout';
import { clampColumnSpan, sortBlocksByStablePosition } from '@/lib/blocks/tree';
import { getBlockUiKey, type PageBlock } from '@/types/block';
import type { DropResolutionContext } from './context-builder';
import { getEventActiveTranslatedRect } from './event-adapters';

const getSortedSiblingsForParent = ({
  blocks,
  context,
  parentId,
  excludeActive = false,
}: {
  blocks: PageBlock[];
  context: DropResolutionContext;
  parentId: string | null;
  excludeActive?: boolean;
}): PageBlock[] =>
  sortBlocksByStablePosition(
    blocks.filter((block) => {
      const sameParent =
        context.getStableParentId(block, context.stableIdByPersistedId) === parentId;
      if (!sameParent) {
        return false;
      }
      return excludeActive ? getBlockUiKey(block) !== context.activeId : true;
    }),
  );

const getDirectionalInsertionIndex = ({
  overIndex,
  indicatorEdge,
}: {
  overIndex: number;
  indicatorEdge: DropResolutionContext['indicatorEdge'];
}): number | null => {
  if (indicatorEdge === 'top' || indicatorEdge === 'left') {
    return overIndex;
  }
  if (indicatorEdge === 'bottom' || indicatorEdge === 'right') {
    return overIndex + 1;
  }
  return null;
};

export const getSameParentReorderInsertionIndex = ({
  context,
  blocks,
  isParentDrop,
}: {
  context: DropResolutionContext;
  blocks: PageBlock[];
  isParentDrop: (data: unknown) => boolean;
}): { oldIndex: number; insertionIndex: number; siblingsWithoutActive: PageBlock[] } | null => {
  const {
    overData,
    overBlock,
    activeParentId,
    targetParentId,
    activeId,
    effectiveOverId,
    indicatorEdge,
  } = context;
  if (isParentDrop(overData) || !overBlock || activeParentId !== targetParentId) {
    return null;
  }

  const siblings = getSortedSiblingsForParent({
    blocks,
    context,
    parentId: activeParentId,
  });
  const oldIndex = siblings.findIndex((block) => getBlockUiKey(block) === activeId);
  if (oldIndex === -1 || effectiveOverId === activeId) {
    return null;
  }

  const siblingsWithoutActive = siblings.filter((block) => getBlockUiKey(block) !== activeId);
  const overIndex = siblingsWithoutActive.findIndex(
    (block) => getBlockUiKey(block) === effectiveOverId,
  );
  if (overIndex === -1) {
    return null;
  }

  const directionalInsertionIndex = getDirectionalInsertionIndex({ overIndex, indicatorEdge });
  let insertionIndex = directionalInsertionIndex ?? overIndex;
  if (directionalInsertionIndex === null) {
    const overIndexInOriginal = siblings.findIndex(
      (block) => getBlockUiKey(block) === effectiveOverId,
    );
    if (overIndexInOriginal === -1) {
      return null;
    }
    insertionIndex = oldIndex < overIndexInOriginal ? overIndex + 1 : overIndex;
  }

  return { oldIndex, insertionIndex, siblingsWithoutActive };
};

export const reorderWithinSameParentIfNeeded = ({
  context,
  blocks,
  isParentDrop,
}: {
  context: DropResolutionContext;
  blocks: PageBlock[];
  isParentDrop: (data: unknown) => boolean;
}): PageBlock[] | null => {
  const result = getSameParentReorderInsertionIndex({ context, blocks, isParentDrop });
  if (!result) {
    return null;
  }

  const { activeBlock } = context;
  const { oldIndex, insertionIndex, siblingsWithoutActive } = result;

  const activeProps = { ...(activeBlock.props ?? {}) };
  delete activeProps[FLOAT_RESTORE_POSITION_PROP];
  const reordered = [...siblingsWithoutActive];
  reordered.splice(insertionIndex, 0, { ...activeBlock, props: activeProps });
  const newIndex = reordered.findIndex((block) => getBlockUiKey(block) === context.activeId);
  if (newIndex === oldIndex) {
    return null;
  }

  const updates = new Map<string, PageBlock>();
  reordered.forEach((block, index) => {
    updates.set(block.id, { ...block, position: index + 1 });
  });

  return blocks.map((block) => updates.get(block.id) || block);
};

export const getMoveInsertionIndex = ({
  context,
  blocks,
  isParentDrop,
}: {
  context: DropResolutionContext;
  blocks: PageBlock[];
  isParentDrop: (data: unknown) => boolean;
}): { insertionIndex: number; targetSiblings: PageBlock[] } => {
  const { targetParentId, overData, overBlock, indicatorEdge, event } = context;
  const targetSiblings = getSortedSiblingsForParent({
    blocks,
    context,
    parentId: targetParentId,
    excludeActive: true,
  });
  let insertionIndex = targetSiblings.length;
  if (!isParentDrop(overData) && overBlock) {
    const overIndex = targetSiblings.findIndex((block) => block.id === overBlock.id);
    if (overIndex >= 0) {
      const directionalInsertionIndex = getDirectionalInsertionIndex({ overIndex, indicatorEdge });
      if (directionalInsertionIndex !== null) {
        insertionIndex = directionalInsertionIndex;
      } else {
        const activeRect = getEventActiveTranslatedRect(event);
        const activeTop = activeRect?.top;
        const activeHeight = activeRect?.height;
        const activeMiddle = activeTop !== undefined ? activeTop + (activeHeight || 0) / 2 : null;
        const overRect = context.over.rect;
        if (!overRect) {
          insertionIndex = overIndex + 1;
        } else {
          const overMiddle = overRect.top + overRect.height / 2;
          insertionIndex =
            activeMiddle !== null && activeMiddle < overMiddle ? overIndex : overIndex + 1;
        }
      }
    }
  }

  return { insertionIndex, targetSiblings };
};

export const moveBlockToTargetParent = ({
  context,
  blocks,
  isParentDrop,
  resolveMovedBlockColumnSpan,
}: {
  context: DropResolutionContext;
  blocks: PageBlock[];
  isParentDrop: (data: unknown) => boolean;
  resolveMovedBlockColumnSpan: (args: {
    activeBlock: PageBlock;
    targetParent: PageBlock | undefined;
    overBlock: PageBlock | undefined;
    indicatorEdge: DropResolutionContext['indicatorEdge'];
    rootParentDirection: DropResolutionContext['rootParentDirection'];
    blocks: PageBlock[];
  }) => number;
}): PageBlock[] => {
  const {
    targetParentId,
    targetParentBlockId,
    overBlock,
    indicatorEdge,
    activeBlock,
    targetParent,
    activeParentId,
    activeParentBlockId,
  } = context;
  const { insertionIndex, targetSiblings } = getMoveInsertionIndex({
    context,
    blocks,
    isParentDrop,
  });

  const movedBlockColumnSpan = resolveMovedBlockColumnSpan({
    activeBlock,
    targetParent,
    overBlock,
    indicatorEdge,
    rootParentDirection: context.rootParentDirection,
    blocks,
  });
  const movedBlockProps = { ...(activeBlock.props ?? {}) };
  delete movedBlockProps[FLOAT_RESTORE_POSITION_PROP];
  if (targetParent?.blockType !== 'group') {
    delete movedBlockProps.float;
  }
  const nextTargetSiblings = [...targetSiblings];
  nextTargetSiblings.splice(insertionIndex, 0, {
    ...activeBlock,
    props: movedBlockProps,
    parentBlockId: targetParentBlockId,
    columnSpan: movedBlockColumnSpan,
  });
  const orderedTargetSiblings =
    activeParentId !== targetParentId &&
    targetParent?.blockType === 'group' &&
    isBlockFloated(activeBlock)
      ? [
          ...nextTargetSiblings.filter(isBlockFloated),
          ...nextTargetSiblings.filter((block) => !isBlockFloated(block)),
        ]
      : nextTargetSiblings;

  const updates = new Map<string, PageBlock>();
  orderedTargetSiblings.forEach((block, index) => {
    updates.set(block.id, {
      ...block,
      parentBlockId: targetParentBlockId,
      position: index + 1,
    });
  });

  if (activeParentId !== targetParentId) {
    const sourceSiblings = getSortedSiblingsForParent({
      blocks,
      context,
      parentId: activeParentId,
      excludeActive: true,
    });
    sourceSiblings.forEach((block, index) => {
      updates.set(block.id, { ...block, position: index + 1 });
    });
  }

  let updatedBlocks = blocks.map((block) => updates.get(block.id) || block);
  updatedBlocks = cleanupParentContainers(updatedBlocks, activeParentBlockId);
  return updatedBlocks;
};

export const canMoveBlockToTargetParent = ({
  context,
  blocks,
  isParentDrop,
  resolveMovedBlockColumnSpan,
}: {
  context: DropResolutionContext;
  blocks: PageBlock[];
  isParentDrop: (data: unknown) => boolean;
  resolveMovedBlockColumnSpan: (args: {
    activeBlock: PageBlock;
    targetParent: PageBlock | undefined;
    overBlock: PageBlock | undefined;
    indicatorEdge: DropResolutionContext['indicatorEdge'];
    rootParentDirection: DropResolutionContext['rootParentDirection'];
    blocks: PageBlock[];
  }) => number;
}): boolean => {
  const {
    activeBlock,
    targetParent,
    overBlock,
    indicatorEdge,
    targetParentBlockId,
    activeParentId,
    targetParentId,
  } = context;
  const { insertionIndex } = getMoveInsertionIndex({ context, blocks, isParentDrop });
  const movedBlockColumnSpan = resolveMovedBlockColumnSpan({
    activeBlock,
    targetParent,
    overBlock,
    indicatorEdge,
    rootParentDirection: context.rootParentDirection,
    blocks,
  });

  if ((activeBlock.parentBlockId ?? null) !== targetParentBlockId) {
    return true;
  }

  if (clampColumnSpan(activeBlock.columnSpan) !== movedBlockColumnSpan) {
    return true;
  }

  if (activeParentId !== targetParentId) {
    return true;
  }

  const sourceSiblings = getSortedSiblingsForParent({
    blocks,
    context,
    parentId: activeParentId,
    excludeActive: true,
  });
  const sourceSiblingsWithActive = getSortedSiblingsForParent({
    blocks,
    context,
    parentId: activeParentId,
  });
  const oldIndex = sourceSiblingsWithActive.findIndex(
    (block) => getBlockUiKey(block) === context.activeId,
  );

  if (oldIndex === -1) {
    return false;
  }

  const nextSiblings = [...sourceSiblings];
  nextSiblings.splice(insertionIndex, 0, activeBlock);
  const newIndex = nextSiblings.findIndex((block) => getBlockUiKey(block) === context.activeId);

  return newIndex !== oldIndex;
};
